<script async setup>
import { defineProps, ref, onMounted, onBeforeUnmount, nextTick } from "vue";
import { tns } from "tiny-slider";

const props = defineProps({
    elementId: {
        type: [Number, String],
        required: true
    },
});

const sliderApi = ref(null);
const slideCount = ref(0);
const visibleItems = ref(0);
const canSlide = ref(false);

const sliderId = `slider-${props.elementId}`;

function matchSlideHeights() {
    const items = document.querySelectorAll(`#${sliderId} .tns-item`);
    let maxHeight = 0;
    items.forEach((item) => {
        const child = item.querySelector("*");
        if (!child) return;
        child.style.height = "auto";
        maxHeight = Math.max(maxHeight, child.offsetHeight);
    });
    items.forEach((item) => {
        const child = item.querySelector("*");
        if (child) child.style.height = `${maxHeight}px`;
    });
}

function updateInfo() {
    const info = sliderApi.value?.getInfo?.();
    if (!info) return;
    slideCount.value = info.slideCount ?? 0;
    visibleItems.value = info.items ?? 0;
    canSlide.value = slideCount.value > visibleItems.value;
}

const initSlider = async () => {
    await nextTick();
    sliderApi.value = tns({
        container: `#slider-${props.elementId}`,
        items: 4,
        slideBy: 1,
        gutter: 20,
        controls: false,
        nav: false,
        mouseDrag: false,
        speed: 400,
        loop: false,
        rewind: true,
        center: false,
        edgePadding: 0,
        responsive: {
            0: { items: 1, fixedWidth: 280, edgePadding: 0 },
            680: { fixedWidth: false, items: 2, gutter: 20, edgePadding: 4 },
            980: { items: 3, gutter: 20, edgePadding: 0 },
            1340: { items: 4, gutter: 20, edgePadding: 0 },
        },
    });
    updateInfo();
    matchSlideHeights();
};

const destroySlider = () => {
    sliderApi.value?.destroy();
    sliderApi.value = null;
};

const prevSlide = () => sliderApi.value?.goTo("prev");
const nextSlide = () => sliderApi.value?.goTo("next");

let lastIsSmall = null;
let resizeRaf = 0;

const handleResize = () => {
    if (resizeRaf) cancelAnimationFrame(resizeRaf);

    resizeRaf = requestAnimationFrame(() => {
        const isSmall = window.innerWidth < 680;

        if (lastIsSmall === null) {
            lastIsSmall = isSmall;
            updateInfo();
            matchSlideHeights();
            return;
        }

        if (isSmall !== lastIsSmall) {
            lastIsSmall = isSmall;
            destroySlider();
            initSlider().then(() => {
                sliderApi.value?.goTo(0);
                updateInfo();
                matchSlideHeights();
            });
        } else {
            sliderApi.value?.refresh?.();
            updateInfo();
            matchSlideHeights();
        }
    });
};

onMounted(async () => {
    await initSlider();
    lastIsSmall = window.innerWidth < 680;
    updateInfo();
    matchSlideHeights();
    window.addEventListener("resize", handleResize, { passive: true });
});

onBeforeUnmount(() => {
    window.removeEventListener("resize", handleResize);
    destroySlider();
});
</script>

<template>
    <div class="relative">
        <div class="overflow-hidden">
            <div class="p-1" :id="'slider-' + props.elementId">
                <slot />
            </div>
        </div>

        <div v-if="canSlide" class="max-w-ts-content mx-auto relative ">
            <div class="flex justify-end mt-4">
                <div class="flex gap-x-4">
                    <button class="cursor-pointer" @click="prevSlide">
                        <kd-icon-arrow-left class="drop-shadow-ts-tight" fill="gray" size="30" />
                    </button>
                    <button class="cursor-pointer" @click="nextSlide">
                        <kd-icon-arrow-right class="drop-shadow-ts-tight" fill="gray" size="30" />
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>